import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Search, Mail, Download, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_LABEL = {
  exit_intent: 'Exit Intent Popup',
  free_guide: 'Free Guide Section',
  unknown: 'Unknown',
};

const AdminEmailLeadsPage = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${API_URL}/api/admin/email-leads`,
        { credentials: 'include' }
      );
      if (res.status === 401) {
        navigate('/admin');
        return;
      }
      if (!res.ok) throw new Error('Failed to fetch email leads');
      const data = await res.json();
      setLeads(data);
    } catch (e) {
      console.error(e);
      setError('Failed to load email leads. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const sources = useMemo(() => {
    const set = new Set(leads.map((l) => l.source || 'unknown'));
    return ['all', ...Array.from(set)];
  }, [leads]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (sourceFilter !== 'all' && (l.source || 'unknown') !== sourceFilter) return false;
      if (!q) return true;
      return (
        (l.email || '').toLowerCase().includes(q) ||
        (l.full_name || '').toLowerCase().includes(q) ||
        (l.phone || '').toLowerCase().includes(q)
      );
    });
  }, [leads, search, sourceFilter]);

  const downloadCSV = () => {
    const header = ['Email', 'Source', 'Full Name', 'Phone', 'Captured At'];
    const rows = filtered.map((l) => [
      l.email || '',
      SOURCE_LABEL[l.source] || l.source || 'Unknown',
      l.full_name || '',
      l.phone || '',
      l.created_at || '',
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tcube_email_leads_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-email-leads-page">
      <header className="bg-white shadow-sm border-b border-[#E2E8F0] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <Link
                to="/admin/dashboard"
                className="text-[#64748B] hover:text-[#1E88E5] inline-flex items-center gap-1 text-sm font-medium"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Link>
              <div>
                <h1 className="text-xl font-bold text-[#1F2933]">Email Leads</h1>
                <p className="text-sm text-[#64748B]">Captured from exit-intent popup &amp; free-guide section</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={downloadCSV}
                disabled={!filtered.length}
                className="inline-flex items-center gap-2 px-3 py-2 bg-[#1E88E5] text-white rounded-lg text-sm font-semibold disabled:opacity-50 hover:bg-[#1976D2]"
                data-testid="download-csv-btn"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Download CSV</span>
              </button>
              <button
                onClick={fetchLeads}
                disabled={loading}
                className="p-2 text-[#64748B] hover:text-[#1E88E5] hover:bg-[#F1F5F9] rounded-lg"
                data-testid="refresh-btn"
                aria-label="Refresh"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5">
            <p className="text-sm text-[#64748B] mb-1">Total Leads</p>
            <p className="text-2xl font-bold text-[#1F2933]" data-testid="lead-count-total">{leads.length}</p>
          </div>
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5">
            <p className="text-sm text-[#64748B] mb-1">From Exit-Intent Popup</p>
            <p className="text-2xl font-bold text-[#1F2933]">
              {leads.filter((l) => l.source === 'exit_intent').length}
            </p>
          </div>
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5">
            <p className="text-sm text-[#64748B] mb-1">From Free Guide Section</p>
            <p className="text-2xl font-bold text-[#1F2933]">
              {leads.filter((l) => l.source === 'free_guide').length}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-4 mb-6 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email, name, or phone…"
              className="w-full bg-[#F8FAFC] border border-[#E2E8F0] focus:border-[#1E88E5] focus:outline-none rounded-lg pl-10 pr-4 py-2.5 text-sm text-[#1F2933]"
              data-testid="search-input"
            />
          </div>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="bg-[#F8FAFC] border border-[#E2E8F0] focus:border-[#1E88E5] focus:outline-none rounded-lg px-4 py-2.5 text-sm text-[#1F2933]"
            data-testid="source-filter"
          >
            {sources.map((s) => (
              <option key={s} value={s}>
                {s === 'all' ? 'All sources' : SOURCE_LABEL[s] || s}
              </option>
            ))}
          </select>
        </div>

        {/* Error / empty / table */}
        {error && (
          <div className="mb-4 flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        {!loading && filtered.length === 0 && !error && (
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-12 text-center">
            <Mail className="w-10 h-10 mx-auto text-[#94A3B8] mb-3" />
            <p className="text-[#1F2933] font-semibold mb-1">No email leads yet</p>
            <p className="text-[#64748B] text-sm">
              Captured emails from the exit-intent popup and free-guide section will appear here.
            </p>
          </div>
        )}

        {filtered.length > 0 && (
          <div className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="email-leads-table">
                <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wider text-[#64748B]">
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Source</th>
                    <th className="px-4 py-3 hidden md:table-cell">Name</th>
                    <th className="px-4 py-3 hidden md:table-cell">Phone</th>
                    <th className="px-4 py-3">Captured</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]">
                  {filtered.map((l) => (
                    <tr key={l.id} className="hover:bg-[#F8FAFC] text-sm" data-testid={`lead-row-${l.id}`}>
                      <td className="px-4 py-3 text-[#1F2933] font-medium break-all">{l.email}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-[#EFF6FF] text-[#1E88E5] text-xs font-semibold">
                          {SOURCE_LABEL[l.source] || l.source || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[#1F2933] hidden md:table-cell">{l.full_name || '-'}</td>
                      <td className="px-4 py-3 text-[#1F2933] hidden md:table-cell">{l.phone || '-'}</td>
                      <td className="px-4 py-3 text-[#64748B] text-xs">
                        {l.created_at ? new Date(l.created_at).toLocaleString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminEmailLeadsPage;
